import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Space,
  Button,
  Spin,
  Alert,
  Tag,
  Row,
  Col,
  List,
  Badge,
  Tooltip,
  Avatar,
  Divider
} from "antd";
import {
  ClockCircleOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  BookOutlined,
  CheckSquareOutlined,
  PlusOutlined,
  UserOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  MailOutlined,
  LinkedinOutlined,
  IdcardOutlined,
  FolderOutlined,
  TagOutlined,
  TeamOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import useCourseDetails from "../Hooks/Courses/useCourseDetails";
import { publishCourse } from "../Services/CourseListService";
import PrerequisitesEditor from "./PrerequisitesEditor";
import PricingEditor from "./PricingEditor";
import { useLocation } from "react-router-dom";
import { ExpertRecommendationLevelEnumLabels } from "Common/Constants/Enums/ExpertRecommendation";


const { Title, Text, Paragraph } = Typography;

const ReadCourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);
  const [editingPrerequisites, setEditingPrerequisites] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { course, loading, error, refetch } = useCourseDetails(courseId, refreshKey);
  const [editingPricing, setEditingPricing] = useState(false);
  const location = useLocation();

 



  console.log("course deta,,,,,,,,,i,", course);

  const handleEditPricing = () => setEditingPricing(true);
  const handleCancelEditPricing = () => setEditingPricing(false);




  const handleBack = () => {
    navigate(`/proAdmin-dashboard/courses`);
  };

  const handlePublishCourse = async () => {
    try {
      if (!course) {
        toast.error("Course details missing");
        return;
      }

      if (!course.canPublish) {
        toast.warning("You cannot publish this course until all requirements are completed.");
        return;
      }

      setPublishing(true);
      await publishCourse(course.id);

      toast.success("Course published successfully!", {
        autoClose: 1500,
        onClose: () => refetch() // Refetch course data after publishing
      });

    } catch (error) {
      console.error("Publish Course Error:", error);
      toast.error(
        error.response?.data?.message || "Failed to publish course"
      );
    } finally {
      setPublishing(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);

    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;

    return `${hoursInt}h ${minutesInt}m`;
  };

  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes <= 0) return "Not specified";

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
    }

    return `${remainingMinutes} Minutes`;
  };

  // Function to check if course duration is greater than 2 hours
const isCourseDurationGreaterThanTwoHours = () => {
  if (!course?.estimatedTime) return false;
  
  const [hours, minutes] = course.estimatedTime.split(":");
  const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
  
  return totalMinutes > 120; // 120 minutes = 2 hours
};

  const handleSavePricing = () => {
    setEditingPricing(false);
    refetch();
  };
  // Logic to convert days to readable Y/M/D
  const formatDuration = (totalDays) => {
    if (!totalDays) return "Not specified";
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = (totalDays % 365) % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    return parts.join(", ");
  };

  const handleEditCourse = () => {
    navigate(`/proAdmin-dashboard/courses/${course.id}/edit`);
  };

  const getStatusTag = (isPublished) => {
    return (
      <Tag
        color={isPublished ? "green" : "orange"}
        icon={isPublished ? <CheckCircleOutlined /> : <SettingOutlined />}
        className="tagStyle"
      >
        {isPublished ? "Published" : "Draft"}
      </Tag>
    );
  };

  const handleModuleClick = (moduleId) => {
    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
  };

  const handleEditModule = (e, moduleId) => {
    e.stopPropagation();
    navigate(`/proAdmin-dashboard/courses/${courseId}/add-module?moduleId=${moduleId}`);
  };

  const handleEditPrerequisites = () => {
    setEditingPrerequisites(true);
  };

  const handleSavePrerequisites = () => {
    setEditingPrerequisites(false);
    refetch(); // Refetch course data to get updated prerequisites
  };

  const handleCancelEditPrerequisites = () => {
    setEditingPrerequisites(false);
  };

  // Get author information from course object
  const getAuthorInfo = () => {
    if (!course) return { name: "Unknown Author", email: "No email provided", linkedin: null };

    if (course.courseAuthor) {
      return {
        name: course.courseAuthor.name || "Unknown Author",
        email: course.courseAuthor.email || "No email provided",
        linkedin: course.courseAuthor.linkedinUrl || course.linkedinUrl || null
      };
    }
    return {
      name: course.authorName || "Unknown Author",
      email: course.authorEmail || "No email provided",
      linkedin: course.authorLinkedIn || course.linkedinUrl || null
    };
  };



  if (loading) {
    return (
      <div className="loadingSpinner">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Course Details"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!course) {
    return (
      <Alert
        message="Course Not Found"
        description="The requested course could not be found."
        type="warning"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  const authorInfo = getAuthorInfo();

  return (
    <Card
      className="card-professional"
      title={
        <Space direction="vertical" size={2}>
          <Title level={2} style={{ margin: 0, color: "white" }}>
            {course?.title || "Loading..."}
          </Title>
          <Space size="middle">
            {getStatusTag(course?.isPublished)}
            <Text style={{ color: "rgba(255,255,255,0.8)" }}>
              Course ID: {course?.id || "Loading..."}
            </Text>
          </Space>
        </Space>
      }
      extra={
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/proAdmin-dashboard/courses`)}
        >
          Back to Courses rr
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>

        {/* Course Information */}
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={16}>

            {/* Course Description */}
            <div
              style={{
                background: 'white',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                marginBottom: '16px',
                color: "black"
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Space>
                  <FileTextOutlined />
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Course Description</span>
                </Space>
              </div>
              <Paragraph style={{ lineHeight: "1.6", margin: 0, color: "#000" }}>
                {course?.description || "No description available"}
              </Paragraph>
            </div>

            {/* Course Modules */}
            <div
              style={{
                background: 'white',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                color: "black"
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <Space>
                  <BookOutlined />
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Course Modules</span>
                  <Badge
                    count={course?.modules?.length || 0}
                    showZero
                    color={course?.modules?.length > 0 ? "blue" : "default"}
                  />
                </Space>

                <Tooltip
                  title={course?.isPublished ? "Cannot add module to published course" : "Add Module"}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={course?.isPublished}
                    onClick={() =>
                      navigate(`/proAdmin-dashboard/courses/${course?.id}/add-module`)
                    }
                  >
                    Add Module
                  </Button>
                </Tooltip>
              </div>

              {/* --- MODULE LIST --- */}
              {course?.modules && course.modules.length > 0 ? (
                <List
                  dataSource={course.modules}
                  renderItem={(module, index) => (
                    <List.Item
                      key={module.id}
                      style={{
                        cursor: "pointer",
                        padding: "12px",
                        border: "1px solid #f0f0f0",
                        borderRadius: "8px",
                        marginBottom: "8px",
                      }}
                      onClick={() => handleModuleClick(module.id)}
                    >
                      <List.Item.Meta
                        avatar={
                          <h4>{index + 1}</h4>
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Space>
                              <Text strong>{module.title || "Untitled Module"}</Text>
                              {module.isFinalized && (
                                <Tag color="green" icon={<CheckCircleOutlined />}>
                                  Finalized
                                </Tag>
                              )}
                            </Space>
                            {!course?.isPublished && (
                              <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={(e) => handleEditModule(e, module.id)}
                              />
                            )}
                          </div>
                        }
                        description={
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <Paragraph
                              ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
                              style={{ margin: 0, color: "#666" }}
                            >
                              {module.description || "No description available"}
                            </Paragraph>
                            <Space size="middle">
                              <Text>
                                <ClockCircleOutlined />{" "}
                                {formatTime(module.estimatedTime)}
                              </Text>
                            

                              {module.hasAssessment ? (
                                <Tag color="green" icon={<CheckCircleOutlined />}>
                                  Assessment
                                </Tag>
                              ) : (
                                <Tag
                                  color="orange"
                                  icon={<ExclamationCircleOutlined />}
                                >
                                  No Assessment
                                </Tag>
                              )}
                            </Space>

                            {/* Alert for modules without assessment */}
                            {!module.hasAssessment && (
                              <Alert
                                message="Assessment Required"
                                description="This module doesn't have an assessment yet. Add an assessment to evaluate student learning."
                                type="warning"
                                showIcon
                                action={
                                  <Button
                                    size="small"
                                    type="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${module.id}/edit-assessment`);
                                    }}
                                  >
                                    Add Assessment
                                  </Button>
                                }
                                style={{
                                  marginTop: '8px',
                                  borderRadius: '6px'
                                }}
                              />
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <BookOutlined
                    style={{
                      fontSize: "48px",
                      color: "#d9d9d9",
                      marginBottom: "16px",
                    }}
                  />
                  <Title level={4} style={{ color: "#666" }}>
                    No Modules Added
                  </Title>
                  <Text style={{ color: "#666" }}>
                    Start by adding your first module
                  </Text>
                  <div style={{ marginTop: "16px" }}>
                    <Tooltip
                      title={course?.isPublished ? "Cannot add module to published course" : "Add Module"}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        disabled={course?.isPublished}
                        onClick={() =>
                          navigate(`/proAdmin-dashboard/courses/${course?.id}/add-module`)
                        }
                      >
                        Add First Module
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* RIGHT SIDE DIVS */}
          <Col xs={24} lg={8}>
            {/* Course Overview */}
            <div
              style={{
                background: 'white',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                marginBottom: '16px',
                color: "black"
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Space>
                  <UserOutlined />
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Course Overview</span>
                </Space>
              </div>

              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                {/* Status */}
                <div className="overview-item">
                  <Text strong>Status:</Text>
                  {getStatusTag(course?.isPublished)}
                </div>

                {/* Duration */}
                <div className="overview-item">
                  <Text strong>Duration:</Text>
                  <Space>
                    <ClockCircleOutlined />
                    <Text>{formatTime(course?.estimatedTime) || "Not specified"}</Text>
                  </Space>
                </div>

                {/* Modules Count */}
                <div className="overview-item">
                  <Text strong>Total Modules:</Text>
                  <Text>{course?.modules?.length || 0}</Text>
                </div>
              </Space>
            </div>

            {/* Pricing Section */}
            {editingPricing ? (
              <PricingEditor
                courseId={courseId}
                initialPricing={course?.pricing}
                isPaid={course?.isPaid}
                hasActiveExpertSupport={course?.hasActiveExpertSupport}
                onSave={handleSavePricing}
                onCancel={handleCancelEditPricing}
              />
            ) : (
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                  marginTop: "12px",
                  color: "black"
                }}
              >
                {/* HEADER WITH EDIT ICON */}
                <div
                  style={{
                    marginBottom: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Space>
                    <TagOutlined />
                    <span style={{ fontWeight: "bold", fontSize: "16px" }}>Pricing</span>
                  </Space>

                  {/* Edit icon only when NOT published */}
                  {!course?.isPublished && (
                    <EditOutlined
                      style={{ fontSize: 16, cursor: "pointer", color: "#1890ff" }}
                      onClick={handleEditPricing}
                    />
                  )}
                </div>

                {/* CONTENT */}
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {/* FREE COURSE */}
                  {!course?.isPaid && (
                    <Tag
                      color="green"
                      style={{
                        alignSelf: "flex-start",
                        fontSize: "14px",
                        padding: "4px 10px",
                        borderRadius: "6px"
                      }}
                    >
                      FREE COURSE
                    </Tag>
                  )}

                  {/* PAID COURSE */}
                  {course?.isPaid && course?.pricing ? (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Text type="secondary">Price</Text>
                        <Text strong style={{ fontSize: "16px", color: "#52c41a" }}>
                          {course.pricing.currency} {course.pricing.price}
                        </Text>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Text type="secondary">Access Duration</Text>
                        <Text>{formatDuration(course.pricing.accessDurationDays)}</Text>
                      </div>


                    </>
                  ) : (
                    <Text type="secondary">Pricing not configured</Text>
                  )}

                  {/* Expert Support Section */}
{course?.isPaid && (
  <>
    <Divider style={{ margin: "12px 0" }} />

    <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Space>
        <TeamOutlined />
        <Text strong>Expert Support</Text>
        {/* Check the boolean from your JSON */}
        {course?.hasActiveExpertSupport ? (
          <Tag color="blue">Active</Tag>
        ) : (
          <Tag color="orange">Not Added</Tag>
        )}
      </Space>

      {/* Edit button: only show if there is actually a support ID to edit */}
      {course?.courseExpertSupport?.id && (
        <EditOutlined
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => navigate(`/proAdmin-dashboard/courses/${course.id}/expert-support/${course.courseExpertSupport.expertServiceId}/edit`)}
        />
      )}
    </div>

    {/* Logic to show Details vs Alert */}
    {course?.courseExpertSupport ? (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Avatar
          src={course.courseExpertSupport.expert?.profileImage}
          icon={<UserOutlined />}
          size="small"
        />
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Text strong>
              {course.courseExpertSupport.expert?.firstName} {course.courseExpertSupport.expert?.lastName}
            </Text>

            {/* SIMPLE RECOMMENDATION TAG */}
            <Tag color="blue">
              {ExpertRecommendationLevelEnumLabels[course.courseExpertSupport.recommendationLevel]}
            </Tag>
          </div>

          <div style={{ fontSize: 12, color: "#666" }}>
            {course.courseExpertSupport.title} — ${course.courseExpertSupport.unitPrice} for {formatMinutesToHours(course.courseExpertSupport.unitDurationMinutes)}
          </div>
          
          {/* Additional details */}
          <div style={{ fontSize: 11, color: "#999" }}>
            {course.courseExpertSupport.allowedUnits} units available
          </div>
        </div>
      </div>
    ) : (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px",
        background: "#f8f9fa",
        borderRadius: "6px",
        border: "1px dashed #d9d9d9"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <TeamOutlined style={{ color: "#8c8c8c" }} />
          <Text type="secondary" style={{ fontSize: "13px" }}>
            Expert support not added
          </Text>
        </div>

        <Button
          type="link"
          size="small"
          style={{ padding: "0", height: "auto" }}
          onClick={() => {
            // Check if course meets requirements for adding expert support
            if (!course?.isPaid) {
              toast.error("Expert support can only be added to paid courses");
              return;
            }
            
            if (!isCourseDurationGreaterThanTwoHours()) {
              toast.error("Expert support can only be added to courses with duration greater than 2 hours");
              return;
            }
            
            navigate(`/proAdmin-dashboard/courses/${course.id}/expert-support/new/edit`);
          }}
        >
          Add Support
        </Button>
      </div>
    )}
  </>
)}


                </Space>
              </div>
            )}

{/* Expert Support Validation Message */}
{course?.isPaid && !course?.hasActiveExpertSupport && !isCourseDurationGreaterThanTwoHours() && (
  <Alert
    message="Course Duration Requirement"
    description="Expert support can only be added to courses with a duration greater than 2 hours. This course currently has a duration of {formatTime(course?.estimatedTime)}."
    type="info"
    showIcon
    style={{ marginTop: 16, fontSize: 12 }}
  />
)}

            {/* Category & Subcategories Card - Added below Course Overview */}
            {course.category && (
              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #f0f0f0'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <Space>
                    <FolderOutlined />
                    <Text strong style={{ fontSize: '15px' }}>Category & Subcategories</Text>
                  </Space>
                </div>

                {/* Category */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <FolderOutlined style={{ color: '#666', fontSize: '14px' }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>Category:</Text>
                  </div>
                  <Text strong style={{ marginLeft: '24px', fontSize: '14px' }}>{course.category.name}</Text>
                </div>

                {/* Subcategories */}
                {course.category.subCategories && course.category.subCategories.length > 0 && (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <TagOutlined style={{ color: '#666', fontSize: '14px' }} />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Subcategories ({course.category.subCategories.length}):
                      </Text>
                    </div>
                    <Space wrap style={{ marginLeft: '24px' }}>
                      {course.category.subCategories.map((sub, index) => (
                        <Tag
                          key={sub.id}
                          style={{
                            fontSize: '12px',
                            padding: '2px 8px',
                            margin: '0 6px 6px 0',
                            borderColor: '#d9d9d9',
                            color: '#000'
                          }}
                        >
                          {sub.name}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </div>
            )}

            {/* Prerequisites Section */}
            {editingPrerequisites ? (
              <PrerequisitesEditor
                courseId={courseId}
                initialPrerequisites={course?.coursePrerequisites || []}
                onSave={handleSavePrerequisites}
                onCancel={handleCancelEditPrerequisites}
              />
            ) : (
              <div
                style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  marginBottom: '16px',
                  color: "black"
                }}
              >
                {/* HEADER WITH EDIT ICON */}
                <div
                  style={{
                    marginBottom: '16px',
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Space>
                    <CheckSquareOutlined />
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Prerequisites</span>
                  </Space>

                  {/* Small Edit Icon */}
                  {!course?.isPublished && (
                    <EditOutlined
                      style={{ fontSize: 16, cursor: "pointer", color: "#1890ff" }}
                      onClick={handleEditPrerequisites}
                    />
                  )}
                </div>

                {/* LIST OR PLACEHOLDER */}
                {course?.coursePrerequisites && course.coursePrerequisites.length > 0 ? (
                  <List
                    size="small"
                    dataSource={course.coursePrerequisites}
                    renderItem={(p) => (
                      <List.Item key={p.id}>
                        <Space>
                          <CheckSquareOutlined style={{ color: "#52c41a" }} />
                          <Text>{p.description || p}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Text type="secondary">No prerequisites specified</Text>
                )}
              </div>
            )}

            {/* Author Information Card */}
            <div style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
              marginBottom: '16px',
              color: "black"
            }}>
              <div style={{ marginBottom: '16px' }}>
                <Space>
                  <IdcardOutlined style={{ color: '#1890ff' }} />
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Author Information</span>
                </Space>
              </div>

              <Space size={24} wrap>
                <Space>
                  <UserOutlined style={{ color: '#666' }} />
                  <Text strong>Name:</Text>
                  <Text>{authorInfo.name}</Text>
                </Space>

                <Space>
                  <MailOutlined style={{ color: '#666' }} />
                  <Text strong>Email:</Text>
                  <Text>{authorInfo.email}</Text>
                </Space>

                <Space>
                  <LinkedinOutlined style={{ color: '#666' }} />
                  <Text strong>LinkedIn:</Text>
                  {authorInfo.linkedin ? (
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(authorInfo.linkedin, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      View
                    </Button>
                  ) : (
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                      Not provided
                    </Text>
                  )}
                </Space>
              </Space>
            </div>

            {/* Quick Actions - Only show for draft courses */}
            {!course?.isPublished && (
              <div
                style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  color: "black"
                }}
              >
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '16px' }}>Quick Actions</Text>
                </div>

                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <Button
                    type="default"
                    block
                    icon={<EditOutlined />}
                    size="large"
                    style={{ marginBottom: '10px' }}
                    onClick={handleEditCourse}
                  >
                    Edit Course
                  </Button>
                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={handlePublishCourse}
                    icon={<CheckCircleOutlined />}
                    loading={publishing}
                  >
                    Publish Course
                  </Button>
                </Space>
              </div>
            )}
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default ReadCourseDetails;